import React, { useState } from 'react';
import { useApp, useAppDispatchers } from '../../../state/app';
import Input from '../../../components/UI/CustomInputField';
import Button from '../../../components/UI/Button';
import Message from '../../../components/UI/Message';
import css from './ConfirmAddress.module.scss';

export interface ConfirmAddressProps {
  onClose: () => void;
  onCancel: () => void;
}

export default function ConfirmAddress({
  onClose,
  onCancel
}: ConfirmAddressProps) {
  const { answer } = useAppDispatchers();
  const { registrationToken } = useApp();
  const [state, setState] = useState({
    address: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    errorMessage: ''
  });

  const handleStateChange = (key: string, val: string) => {
    setState(formState => ({ ...formState, [key]: val }));
  };

  const errorChecking = () => {
    if (state.address.trim().length < 2) {
      handleStateChange('errorMessage', 'Please enter a valid street address.');
      return true;
    }
    if (state.city.trim().length < 2) {
      handleStateChange('errorMessage', 'Please enter a valid city.');
      return true;
    }
    if (state.state.trim().length < 2) {
      handleStateChange('errorMessage', 'Please enter a valid state.');
      return true;
    }
    if (state.zip.trim().length < 5 || state.zip.length > 10) {
      handleStateChange('errorMessage', 'Please enter a valid zip code.');
      return true;
    }
    return false;
  };

  const updateBillingAddress = () => {
    if (!errorChecking()) {
      answer({
        accessToken: registrationToken,
        questions: ['set_billing_address'],
        answers: [
          {
            street_address: state.address,
            street_address_2: state.line2,
            city: state.city,
            state: state.state,
            postal_code: state.zip
          }
        ]
      });
      onClose();
    }
  };

  const isDisabled = () => !state.address || !state.city || !state.state;

  return (
    <div className={css.confirmAddress}>
      <div className={css.confirmAddressModal}>
        <Message text={state.errorMessage} error={true} />
        <h1 className={css.modalHeader}>Debit Card Billing Address</h1>

        <form className={css.confirmAddressForm}>
          <Input
            id="street-address"
            placeholder="Street address"
            autoComplete="off"
            autoCorrect="off"
            type="text"
            value={state.address}
            onChange={(value: string) => handleStateChange('address', value)}
          />

          <Input
            id="street-address-2"
            placeholder="Apartment or suite (optional)"
            autoComplete="off"
            autoCorrect="off"
            type="text"
            value={state.line2}
            onChange={(value: string) => handleStateChange('line2', value)}
          />
          <div className={css.confirmAddressDetails}>
            <div className={css.confirmAddressInput}>
              <Input
                id="city"
                placeholder="City"
                autoComplete="off"
                autoCorrect="off"
                type="text"
                value={state.city}
                onChange={(value: string) => handleStateChange('city', value)}
                data-e2e="address-zip"
              />
            </div>
            <div className={css.confirmAddressInput}>
              <Input
                id="state"
                placeholder="State"
                autoComplete="off"
                autoCorrect="off"
                type="text"
                value={state.state}
                onChange={(value: string) => handleStateChange('state', value)}
              />
            </div>
            <div className={css.confirmAddressInput}>
              <Input
                id="zip"
                placeholder="Zip code"
                autoComplete="off"
                autoCorrect="off"
                type="text"
                value={state.zip}
                onChange={(value: string) => handleStateChange('zip', value)}
              />
            </div>
          </div>
        </form>

        <div className={css.confirmAddressButtons}>
          <Button
            destructive={true}
            onClick={onCancel}
            text="Cancel"
            data-e2e="cancel-place"
          />
          <Button
            disabled={isDisabled()}
            onClick={updateBillingAddress}
            text="Confirm"
            data-e2e="confirm-place"
          />
        </div>
      </div>
    </div>
  );
}
